import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { useAuth } from '@/contexts/AuthContext';

// Storage key for post-login redirect
const NEXT_URL_KEY = 'greenfirefly_next_url';

// Provider icons
const GoogleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24">
    <path
      fill="currentColor"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="currentColor"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="currentColor"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
    />
    <path
      fill="currentColor"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
);

const GitHubIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
  </svg>
);

interface ProviderInfo {
  providers: string[];
  dev_mode: boolean;
  password_enabled: boolean;
}

/**
 * Get the stored next URL (post-login redirect destination).
 * Returns the URL and clears it from storage.
 */
export function getAndClearNextUrl(): string | null {
  const nextUrl = sessionStorage.getItem(NEXT_URL_KEY);
  if (nextUrl) {
    sessionStorage.removeItem(NEXT_URL_KEY);
  }
  return nextUrl;
}

/**
 * Store a next URL for post-login redirect.
 */
export function setNextUrl(url: string): void {
  sessionStorage.setItem(NEXT_URL_KEY, url);
}

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, isLoading, login, devLogin, refreshSession } = useAuth();
  const [providers, setProviders] = useState<ProviderInfo | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Password login state
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isPasswordLoading, setIsPasswordLoading] = useState(false);

  // Store next URL from location state or query param
  useEffect(() => {
    // Priority: location state > query param > existing sessionStorage
    const fromState = (location.state as { from?: { pathname: string; search?: string } })?.from;
    const nextParam = searchParams.get('next');

    if (fromState?.pathname) {
      const nextUrl = fromState.pathname + (fromState.search || '');
      setNextUrl(nextUrl);
    } else if (nextParam) {
      setNextUrl(nextParam);
    }
  }, [location.state, searchParams]);

  // Check for error from OAuth callback
  useEffect(() => {
    const errorParam = searchParams.get('error');
    if (errorParam) {
      switch (errorParam) {
        case 'authentication_failed':
          const detail = searchParams.get('detail');
          setError(`Authentication failed${detail ? ` (${detail})` : ''}. Please try again.`);
          break;
        case 'access_denied':
          setError('Access was denied. Please try again.');
          break;
        case 'invite_required':
          setError('This app is invite-only. Please contact an admin for an invite.');
          break;
        case 'invalid_invite':
          setError('Invalid or expired invite. Please request a new invite.');
          break;
        case 'email_mismatch':
          setError('Your email does not match the invite. Please use the invited email address.');
          break;
        case 'session_expired':
          setError('Your session expired. Please try signing in again.');
          break;
        case 'provider_not_configured':
          setError('This authentication provider is not configured. Please contact an admin.');
          break;
        default:
          setError(`Authentication error: ${errorParam}`);
      }
    }
  }, [searchParams]);

  // Redirect if already authenticated
  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      const nextUrl = getAndClearNextUrl();
      navigate(nextUrl || '/home');
    }
  }, [isAuthenticated, isLoading, navigate]);

  // Fetch available providers
  useEffect(() => {
    async function fetchProviders() {
      try {
        const response = await fetch('/api/auth/providers');
        if (response.ok) {
          const data = await response.json();
          setProviders(data);
        }
      } catch (err) {
        console.error('Failed to fetch providers:', err);
      }
    }
    fetchProviders();
  }, []);

  // Password login handler
  const handlePasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsPasswordLoading(true);

    try {
      const response = await fetch('/api/auth/login/password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ username, password }),
      });

      const data = await response.json();

      if (data.success) {
        // Refresh session to get user data
        await refreshSession();
        if (data.needs_onboarding) {
          // Keep next URL in sessionStorage for post-onboarding redirect
          navigate('/onboarding');
        } else {
          const nextUrl = getAndClearNextUrl();
          navigate(nextUrl || '/home');
        }
      } else {
        setError(data.error || 'Login failed');
      }
    } catch (err) {
      console.error('Password login error:', err);
      setError('An error occurred. Please try again.');
    } finally {
      setIsPasswordLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">Welcome</CardTitle>
          <CardDescription>
            Sign in to access your agents and evaluations
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Dev mode login */}
          {providers?.dev_mode && (
            <Button
              variant="outline"
              className="w-full"
              onClick={devLogin}
            >
              <span className="mr-2">🔧</span>
              Continue as Dev User
            </Button>
          )}

          {/* OAuth providers */}
          {providers?.providers.includes('google') && (
            <Button
              variant="outline"
              className="w-full"
              onClick={() => login('google')}
            >
              <GoogleIcon />
              <span className="ml-2">Continue with Google</span>
            </Button>
          )}

          {providers?.providers.includes('github') && (
            <Button
              variant="outline"
              className="w-full"
              onClick={() => login('github')}
            >
              <GitHubIcon />
              <span className="ml-2">Continue with GitHub</span>
            </Button>
          )}

          {/* Password login form */}
          {providers?.password_enabled && (
            <>
              {(providers.providers.length > 0 || providers.dev_mode) && (
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <Separator className="w-full" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-card px-2 text-muted-foreground">
                      Or continue with email
                    </span>
                  </div>
                </div>
              )}

              <form onSubmit={handlePasswordLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="username">Username or Email</Label>
                  <Input
                    id="username"
                    type="text"
                    placeholder="Enter username or email"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                    disabled={isPasswordLoading}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="Enter password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={isPasswordLoading}
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full"
                  disabled={isPasswordLoading}
                >
                  {isPasswordLoading ? 'Signing in...' : 'Sign in'}
                </Button>
              </form>
            </>
          )}

          {/* Show message if no providers configured */}
          {providers && providers.providers.length === 0 && !providers.dev_mode && !providers.password_enabled && (
            <div className="text-center text-sm text-muted-foreground">
              No authentication providers configured.
              <br />
              Set AUTH_DEV_MODE=true for development.
            </div>
          )}

          {/* Loading state for providers */}
          {!providers && (
            <div className="text-center text-sm text-muted-foreground">
              Loading authentication options...
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default LoginPage;
